import React, { useEffect, useRef, useState } from "react";

import styles from "styles.module.css";
import {
	gridColumnItemsHandler,
	landingDetailPageGridItems,
} from "./utils/GridColumns";
import Filter from "./Filter";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
//@ts-ignore
import "./styles.css";
import { addMonths } from "date-fns";
import OverLayLoading from "ui/Loading";
import {
	getLandingNoteLinesApi,
	getLandingNotesApi,
	LandingNoteFilter,
	importLandingNote,
	deleteLandingNoteApi,
} from "api/landingNote";
import GridCustomPage, { ColumnButtonProps } from "ui/grid/GridPage";
import { useTranslation } from "react-i18next";
import { GridCellProps } from "@progress/kendo-react-grid";
import { ILandingNote } from "interface/landingNote";
import { PageTypePath } from "utils/pages";
import { ICON_NAMES, COLOR } from "utils/constant";
import CustomDetailedPage from "ui/grid/DetailPage";
import { useNavigate, useLocation } from "react-router";
import CustomPopup from "ui/modal/Popup";
import CustomModal from "ui/modal/Modal";
import CustomButton, { TextButton } from "ui/Button";
import { Loading } from "ui/Loading";
import { usePageAccess } from "hooks/usePageAccess";
import { IconButtonActionType } from "interface";
import LandingNotesMobileList from "./LandingNotesMobileList";
import { set } from "lodash";

const pageSize = 60;

const LandingNoteList = () => {
	const { t } = useTranslation();

	const overlayRef = useRef<HTMLDivElement>(null);
	const checkItemsRef = useRef<HTMLDivElement>(null);
	const [checkedIds, setCheckIds] = useState<string[]>([]);
	const [showSelectedIds, setShowSelectedIds] = useState(false);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1600);
	const [mobileData, setMobileData] = useState<ILandingNote[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);
	const mobileNextSkip = useRef(0);

	const gridRef = useRef<HTMLDivElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const noteRef = useRef<ILandingNote | null>(null);
	const navigate = useNavigate();
	const [filterOptions, setFilterOptions] = useState<LandingNoteFilter>({
		take: pageSize,
		skip: 0,
		fromDate: addMonths(new Date(), -6),
		toDate: new Date(),
	});
	const [forceUpdate, setForceUpdate] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const pendingDeleteNoteNumber = useRef<number | null>(null);

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	const { pageAccess, loading } = usePageAccess();

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const requestMobileData = (skip: number, take: number, resetData = false) => {
		if (mobileRequestInProgress.current || mobileDataFinished.current) {
			return;
		}

		mobileRequestInProgress.current = true;
		setMobileLoading(true);

		getLandingNotesApi({
			...filterOptions,
			skip,
			take,
		})
			.then((response) => {
				const incoming = (response?.data || []) as ILandingNote[];

				if (incoming.length === 0) {
					mobileDataFinished.current = true;
					if (resetData) {
						setMobileData([]);
					}
					return;
				}

				mobileNextSkip.current = skip + take;

				if (resetData) {
					setMobileData(incoming);
				} else {
					setMobileData((prev) => [...prev, ...incoming]);
				}

				const loadedCount = skip + incoming.length;
				if (loadedCount >= (response?.totalCount || 0)) {
					mobileDataFinished.current = true;
				}
			})
			.finally(() => {
				mobileRequestInProgress.current = false;
				setMobileLoading(false);
			});
	};

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 1600);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);

		requestMobileData(0, pageSize, true);
	}, [isMobileView, filterOptions, forceUpdate]);

	const openEdit = (prop: GridCellProps) => {
		if (prop.dataItem.noteStatusId === 4) {
			navigate(`/${PageTypePath.Signature}/${prop.dataItem.noteNumber}`, {
				state: {
					quotaTypeCode: prop.dataItem.quotaTypeCode,
					noteType: 1, // 0 for sales note 1 for landing note
				},
			});
			return;
		}
		const step = prop.dataItem.currentStep;

	//	navigate(
	//		`/${PageTypePath.LandingNoteForm}/${prop.dataItem.noteNumber}/${step}`,
	//	);
		window.open(
			`/${PageTypePath.LandingNoteForm}/${prop.dataItem.noteNumber}/${step}`,
			'_blank'
		);
	};

	const openPdf = (prop: GridCellProps) => {
		localStorage.setItem("noteNumber", prop.dataItem.noteNumber);
		localStorage.setItem("pdfType", "LandingNote");

		window.open(`/${PageTypePath.Pdf}/${prop.dataItem.reportGuid}`, "_blank");
		return;
	};

	const openEditByItem = (item: ILandingNote & Record<string, any>) => {
		if (item.noteStatusId === 4) {
			navigate(`/${PageTypePath.Signature}/${item.noteNumber}`, {
				state: {
					quotaTypeCode: item.quotaTypeCode,
					noteType: 1,
				},
			});
			return;
		}

		const step = item.currentStep;
		//navigate(`/${PageTypePath.LandingNoteForm}/${item.noteNumber}/${step}`);
		window.open(
			`/${PageTypePath.LandingNoteForm}/${item.noteNumber}/${step}`,
			'_blank'
		);

	};

	const openPdfByItem = (item: ILandingNote) => {
		localStorage.setItem("noteNumber", item.noteNumber);
		localStorage.setItem("pdfType", "LandingNote");
		window.open(`/${PageTypePath.Pdf}/${item.reportGuid}`, "_blank");
	};

	const deleteNote = (prop: GridCellProps) => {
		pendingDeleteNoteNumber.current = prop.dataItem.noteNumber;
		setShowDeleteConfirm(true);
	};

	const confirmDeleteNote = () => {
		if (pendingDeleteNoteNumber.current === null) return;
		deleteLandingNoteApi(pendingDeleteNoteNumber.current)
			.then(() => {
				setFilterOptions((prev) => ({ ...prev }));
			})
			.catch((error) => {
				console.error("Failed to delete note", error);
			})
			.finally(() => {
				pendingDeleteNoteNumber.current = null;
				setShowDeleteConfirm(false);
			});
	};

	const itemAlreadyChecked = (id: string) => {
		return checkedIds.includes(id);
	};

	const toggleCheckedId = (id: string, status: number) => {
		if (!handleCanCheck(status)) {
			return;
		}

		setCheckIds((prev) => {
			if (prev.includes(id)) {
				return prev.filter((item) => item !== id);
			}

			return [...prev, id];
		});
	};

	const handleSelectedId = (id: string, noComma: boolean = true): string => {
		const firstPart = id.slice(0, 2);
		const secondPart = id.slice(2);

		const numberPart = Number(secondPart);
		return `${firstPart}-${numberPart}${noComma ? "" : ","}`;
	};

	const copyAllToClipboard = () => {
		const idsToCopy = checkedIds.map((id) => handleSelectedId(id)).join(", ");
		navigator.clipboard.writeText(idsToCopy).then(
			() => {
			},
			(err) => {
				console.error("Could not copy text: ", err);
			},
		);
		setShowSelectedIds(!showSelectedIds);
	};

	const upload = (prop: GridCellProps) => {
		noteRef.current = prop.dataItem;
		fileInputRef.current?.click();
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		const note = noteRef.current;
		if (!file || note === null) return;

		if (overlayRef.current) overlayRef.current.style.display = "block";

		importLandingNote(file, note.noteNumber as unknown as number)
			.finally(() => {
				if (overlayRef.current) overlayRef.current.style.display = "none";
				if (fileInputRef.current) fileInputRef.current.value = "";
				setForceUpdate((prev) => !prev);
			})
			.then((response) => {
				openEditByItem(note);
				noteRef.current = null;
			});
	};

	const handleDeleteConfirmed = () => {
		if (pendingDeleteNoteNumber.current === null) {
			return;
		}

		deleteLandingNoteApi(pendingDeleteNoteNumber.current)
			.then(() => {
				setForceUpdate((prev) => !prev);
			})
			.finally(() => {
				setShowDeleteConfirm(false);
				pendingDeleteNoteNumber.current = null;
			});
	};

	const handleDelete = (noteNumber: number) => {
		setShowDeleteConfirm(true);
		pendingDeleteNoteNumber.current = noteNumber;
	};

	if (!pageAccess) {
		return <Loading show={true} />;
	}

	return (
		<div className={styles.PageContainer}>
			<input
				type="file"
				ref={fileInputRef}
				style={{ display: "none" }}
				onChange={handleFileChange}
			/>
			<div ref={overlayRef} style={{ display: "none" }}>
				<OverLayLoading />
			</div>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>{t("LandingNotes")}</legend>
			</div>
			{showDeleteConfirm && (
				<CustomModal
					title={t("Delete")}
					onClose={() => setShowDeleteConfirm(false)}
					actions={[
						{
							label: t("Cancel"),
							onClick: () => setShowDeleteConfirm(false),
							bgColor: COLOR.Green,
							color: "white",
						},
						{
							label: t("Yes"),
							onClick: confirmDeleteNote,
							bgColor: COLOR.StatusRed,
							color: "white",
						},
					]}
				>
					<p style={{ padding: "16px 0" }}>{t("DeleteNoteConfirmation")}</p>
				</CustomModal>
			)}


			<div ref={checkItemsRef}>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
					isMobileView={true}
					handleShowSelectedIds={() => setShowSelectedIds(!showSelectedIds)}
				/>
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				{showSelectedIds && (
					<CustomPopup
						anchor={checkItemsRef}
						show={showSelectedIds}
						style={{ minWidth: "400px" }}
						onClose={() => setShowSelectedIds(!showSelectedIds)}
					>
						<div
							style={{
								padding: "10px",
								display: "flex",
								justifyContent: "space-between",
								height: "50px",
								fontSize: "16px",
								fontWeight: "bold",
								fontFamily: "Work Sans",
							}}
						>
							<p> {t("CatchCertificate") + "string"}</p>
							<TextButton
								text={"X"}
								onClick={() => setShowSelectedIds(!showSelectedIds)}
								textStyle={{ paddingTop: "16px" }}
							/>
						</div>
						<div
							style={{
								padding: "10px",
								display: "flex",
								flexWrap: "wrap",
								maxWidth: "90%",
								gap: "10px",
							}}
						>
							{checkedIds.map((id) => {
								const isLast = checkedIds.indexOf(id) === checkedIds.length - 1;
								return <div key={id}>{handleSelectedId(id, isLast)}</div>;
							})}
						</div>
						<div
							style={{
								padding: "10px",
								display: "flex",
								justifyContent: "center",
							}}
						>
							<CustomButton
								onClick={copyAllToClipboard}
								label={"Kopier alle"}
							/>
						</div>
					</CustomPopup>
				)}
				{isMobileView ? (
					<LandingNotesMobileList
						items={mobileData}
						loading={mobileLoading}
						onEdit={(item) =>
							openEditByItem(item as ILandingNote & Record<string, any>)
						}
						onOpenPdf={(item) => openPdfByItem(item)}
						onToggleChecked={(item) =>
							toggleCheckedId(item.noteNumber, item.noteStatusId)
						}
						isChecked={(id) => itemAlreadyChecked(id)}
						canCheck={(status) => handleCanCheck(status)}
						onLoadMore={() =>
							requestMobileData(mobileNextSkip.current, pageSize)
						}
						onUpload={(item) => {
							noteRef.current = item;
							fileInputRef.current?.click();
						}}
					/>
				) : (
					<GridCustomPage
						dataKey="noteNumber"
						gridColumns={gridColumnItemsHandler()}
						// detailPage={DetailedPage}
						detailPage={(prop) => (
							<CustomDetailedPage
								grids={landingDetailPageGridItems}
								dataApi={() =>
									getLandingNoteLinesApi({
										noteNumber: prop.dataItem.noteNumber,
										source: prop.dataItem.source,
									})
								}
							/>
						)}
						filter={filterOptions}
						exportGridAsPDFRef={pdfRef}
						pageAccess={pageAccess}
						lastColWidth="265px"
						itemAboveHeight={0}
						exportGridAsExcelRef={excelRef}
						forceUpdate={forceUpdate}
						apiMethod={getLandingNotesApi}
						lastColBtns={[
							{
								iconName: ICON_NAMES.Upload,
								title: t("UploadNoteFile"),
								conditionField: "noteStatusId",
								showConditionValue: 1,
								onclick: upload,
								actionType: IconButtonActionType.Update,
							},
							{
								iconName: ICON_NAMES.Edit,
								title: t("EditNote"),
								conditionField: "noteStatusId",
								showConditionValue: 1,
								onclick: openEdit,
								actionType: IconButtonActionType.Update,
							},

							{
								iconName: ICON_NAMES.Edit,
								title: t("EditNote"),
								conditionField: "noteStatusId",
								showConditionValue: 2,
								onclick: openEdit,
								actionType: IconButtonActionType.Update,
							},
							{
								iconName: ICON_NAMES.Edit,
								title: t("Correction"),
								conditionField: "noteStatusId",
								showConditionValue: 3,
								onclick: openEdit,
								actionType: IconButtonActionType.Update,
							},

							{
								iconName: ICON_NAMES.Signature,
								title: t("SignNote"),
								conditionField: "noteStatusId",
								showConditionValue: 4,
								onclick: openEdit,
								actionType: IconButtonActionType.Update,
							},
							{
								iconName: ICON_NAMES.Delete,
								title: t("Delete"),
								actionType: IconButtonActionType.Delete,
								handler: (prop) => {
									const statusId = prop.dataItem.noteStatusId;
									const visible = statusId === 1 || statusId === 2;
									return {
										onclick: deleteNote,
										alwaysShow: true,
										hidden: !visible,
									};
								},
							},
							{
								iconName: ICON_NAMES.Download,
								title: t("DownloadPdf"),
								alwaysShow: true,
								onclick: openPdf,
							},
							{
								iconName: ICON_NAMES.CheckboxBlack,
								title: t("MarkForCC"),
								alwaysShow: true,

								handler: (prop) => {
									const colItem: ColumnButtonProps = {
										iconName: itemAlreadyChecked(prop.dataItem.noteNumber)
											? ICON_NAMES.Checkbox
											: ICON_NAMES.CheckboxBlack,
										style: {
											opacity: handleCanCheck(prop.dataItem.noteStatusId)
												? 1
												: 0.2,
										},
									};

									colItem.onclick = () =>
										toggleCheckedId(
											prop.dataItem.noteNumber,
											prop.dataItem.noteStatusId,
										);

									return colItem;
								},
							},
						]}
					/>
				)}
			</div>
		{/*	<CustomPopup*/}
		{/*		show={showDeleteConfirm}*/}
		{/*		anchor={gridRef}*/}
		{/*		onClose={() => setShowDeleteConfirm(false)}*/}
		{/*		style={{ minWidth: "300px" }}*/}
		{/*	>*/}
		{/*		<div*/}
		{/*			style={{*/}
		{/*				padding: "10px",*/}
		{/*				display: "flex",*/}
		{/*				justifyContent: "space-between",*/}
		{/*				height: "50px",*/}
		{/*				fontSize: "16px",*/}
		{/*				fontWeight: "bold",*/}
		{/*				fontFamily: "Work Sans",*/}
		{/*			}}*/}
		{/*		>*/}
		{/*			<p> {t("ConfirmDelete")}</p>*/}
		{/*		</div>*/}
		{/*		<div*/}
		{/*			style={{*/}
		{/*				padding: "10px",*/}
		{/*				display: "flex",*/}
		{/*				justifyContent: "center",*/}
		{/*				gap: "10px",*/}
		{/*			}}*/}
		{/*		>*/}
		{/*			<CustomButton*/}
		{/*				onClick={handleDeleteConfirmed}*/}
		{/*				label={t("Confirm")}*/}
		{/*			/>*/}
		{/*			<CustomButton*/}
		{/*				onClick={() => setShowDeleteConfirm(false)}*/}
		{/*				label={t("Cancel")}*/}
		{/*			/>*/}
		{/*		</div>*/}
		{/*	</CustomPopup>*/}
		</div>
	);
};

export default LandingNoteList;

const handleCanCheck = (status: number) => {
	if (status === 1) return false;
	if (status === 2) return false;
	return true;
};
